import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, euro, formatApiError } from "@/lib/api";

const STATUSES = ["new", "confirmed", "completed", "cancelled"];
const STATUS_STYLE = {
  new: "bg-gold-light text-gold-hover border-gold-border",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};
const PAY = { paid: "Paid online", pay_driver: "Pay driver", pending: "Payment pending", refunded: "Refunded" };

const Details = ({ b }) => {
  const rows = [
    ["Reference", b.ref], ["Customer", b.name], ["Email", b.email], ["Phone", `${b.phone_code} ${b.phone}`],
    ["Pickup", `${b.pickup}${b.pickup_details ? `, ${b.pickup_details}` : ""}`],
    ["Drop-off", `${b.dropoff}${b.dropoff_details ? `, ${b.dropoff_details}` : ""}`],
    ["Date & time", `${b.date} ${b.time}`], ["Flight / ferry", b.flight_number || "-"], ["Passengers", b.passengers],
    ["Luggage", b.luggage], ["Child seats", b.child_seats ? b.child_seat_count : "No"],
    ["Name sign", b.meet_greet ? b.name_sign || b.name : "No"], ["Price", euro(b.price)],
    ["Payment", PAY[b.payment_status] || b.payment_status], ["Notes", b.notes || "-"], ["Created", new Date(b.created_at).toLocaleString()],
  ];
  return (
    <div className="divide-y divide-slate-100 text-sm" data-testid="admin-booking-details">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-6 py-2"><span className="text-slate-500">{k}</span><span className="text-right text-ink">{v}</span></div>
      ))}
    </div>
  );
};

export const BookingsTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/bookings").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
      toast.success(`Booking marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shown = filter === "all" ? items : items.filter((b) => b.status === filter);
  const count = (s) => items.filter((b) => b.status === s).length;

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex flex-wrap gap-2">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
            className={`rounded-full border px-4 py-2 text-sm capitalize ${filter === s ? "border-ink bg-ink text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {s} <span className="opacity-60">({s === "all" ? items.length : count(s)})</span>
          </button>
        ))}
      </div>
      <div className="card-soft mt-5 overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] text-slate-500">
            <tr>{["Ref", "When", "Route", "Pax", "Customer", "Price", "Payment", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500" data-testid="admin-bookings-empty">No bookings yet.</td></tr>}
            {shown.map((b) => (
              <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-booking-row-${b.ref}`}>
                <td className="px-4 py-3 font-medium text-ink">{b.ref}</td>
                <td className="px-4 py-3 whitespace-nowrap">{b.date} · {b.time}</td>
                <td className="px-4 py-3">{b.pickup} → {b.dropoff}</td>
                <td className="px-4 py-3">{b.passengers}</td>
                <td className="px-4 py-3">{b.name}</td>
                <td className="px-4 py-3 whitespace-nowrap">{euro(b.price)}</td>
                <td className="px-4 py-3 whitespace-nowrap">{PAY[b.payment_status] || b.payment_status}</td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={(e) => update(b.id, e.target.value)} data-testid="admin-booking-status-select"
                    className={`rounded-full border px-3 py-1.5 text-xs capitalize ${STATUS_STYLE[b.status]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3"><button onClick={() => setOpen(b)} className="text-sm underline decoration-gold underline-offset-4" data-testid="admin-booking-view-button">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Booking {open?.ref}</DialogTitle></DialogHeader>
          {open && <Details b={open} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
